import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  BrowserPermission,
  BrowserService,
  DataAccessBrowserService,
  FzGoogleTagManagerService,
} from '@fantaz/shared/data-access/api';
import {
  GAEventKeys,
  GoogleAnalyticsEvent,
  Location,
} from '@fantaz/shared/data-access/models';
import { ModalAlertComponent } from '@fantaz/shared/ui/modal-alert';
import { Observable, catchError, map, mergeMap, of, tap } from 'rxjs';
import { TrackError } from './track-error.service';

@Injectable({
  providedIn: 'root',
})
export class LocationService {
  dialog: MatDialog = inject(MatDialog);
  browserService: BrowserService = inject(DataAccessBrowserService);
  gtmService: FzGoogleTagManagerService = inject(FzGoogleTagManagerService);

  /**
   * Cache config
   */
  cacheConfig: {
    enabled: boolean;
    cacheTime: number;
    data?: {
      latestTimeLocationShared: number;
      latestResponse: {
        deviceGPS?: Location | null;
        userAllowToUseFallbackLocation?: boolean;
      };
    };
  } = {
    enabled: true,
    cacheTime: 1000 * 60 * 5,
  };

  constructor() {
    this.restoreCache();
  }

  restoreCache() {
    const cacheConfigStr = localStorage.getItem('location_cache_config');

    if (cacheConfigStr) {
      try {
        this.cacheConfig = JSON.parse(cacheConfigStr);
      } catch (error) {
        // report to dynatrace the error
        TrackError.trackError(error as Error);
      }
    }
  }

  persistCache(data: any) {
    localStorage.setItem(
      'location_cache_config',
      JSON.stringify({
        ...this.cacheConfig,
        ...data,
      }),
    );
  }

  public observeLocation(): Observable<{
    deviceGPS?: Location | null;
    userAllowToUseFallbackLocation?: boolean;
  }> {
    // verify if cache is enabled and if the cache is valid to return the
    // cached value
    const dif =
      new Date().getTime() -
      (this.cacheConfig.data?.latestTimeLocationShared || 0);

    if (
      this.cacheConfig.enabled &&
      dif <= this.cacheConfig.cacheTime &&
      this.cacheConfig.data?.latestResponse
    ) {
      // TODO: Report to GA the event of using cache for location
      return of(this.cacheConfig.data?.latestResponse);
    }

    return this.browserService.getLocationPermission().pipe(
      mergeMap((res) => {
        //
        // If permission is granted or prompt, then get location from browser
        //
        if (
          [BrowserPermission.granted, BrowserPermission.prompt].includes(res)
        ) {
          return this.browserService.getLocation().pipe(
            map((position) => {
              return {
                deviceGPS: {
                  Latitude: position.coords.latitude,
                  Longitude: position.coords.longitude,
                  Altitude: position.coords.altitude,
                  Speed: position.coords.speed,
                  DateTime: position.timestamp,
                } as Location,
              };
            }),
            catchError((err) => {
              // report to dynatrace the error
              TrackError.trackError(err);

              // report to GA the event
              this.sendGAEventLocation(
                GAEventKeys.USER_LOCATION_SHARED_FAIL,
                'location_denied',
              );

              // continue with the flow
              return of({
                deviceGPS: null,
              });
            }),
          );
        } else {
          // report to GA the event
          this.sendGAEventLocation(
            GAEventKeys.USER_LOCATION_INACCESSIBLE,
            'location_denied_by_system',
          );
        }

        return of({
          deviceGPS: null,
        });
      }),
      mergeMap((location) => {
        if (location?.deviceGPS) {
          return of({
            ...location,
          });
        }
        const userAllowedFallbackConsent =
          this.browserService.userAllowedFallbackConsent();

        if (userAllowedFallbackConsent) {
          return of({
            userAllowToUseFallbackLocation: true,
          });
        }

        // request user permission
        const dialogRef = this.dialog.open(ModalAlertComponent, {
          data: {
            modalType: 'location-disabled',
            required: true,
          },
          disableClose: true,
          restoreFocus: true,
        });

        return dialogRef.afterClosed().pipe(
          map((result: boolean) => {
            // store user allowed permission
            this.browserService.setUserAllowedFallbackConsent(result);

            // report to GA the event
            this.sendGAEventLocation(
              GAEventKeys.USER_LOCATION_SHARED,
              'forced_by_user',
            );

            // return permission result
            return {
              userAllowToUseFallbackLocation: result,
            };
          }),
        );
      }),
      map((result) => {
        return result;
      }),
      tap((result) => {
        this.persistCache({
          data: {
            latestTimeLocationShared: new Date().getTime(),
            latestResponse: {
              ...result,
            },
          },
        });
      }),
    );
  }

  async sendGAEventLocation(eventName: GAEventKeys, type: string) {
    // TODO: Refactor this to use the new gtm service
    const sessionString = localStorage.getItem('user_session');
    if (sessionString) {
      const session = JSON.parse(sessionString);
      const {
        tokens: {
          idToken: { payload },
        },
      } = session;
      const event = {
        event: 'user_location_status',
        location_action: eventName,
        user_id: payload.sub,
        user_name: localStorage.getItem('username'),
        location_details: type,
        timestamp: new Date().getTime(),
      } as GoogleAnalyticsEvent;

      await this.gtmService.pushTag(event);
    }
  }
}
